import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Download, Facebook, Twitter, Linkedin, Link as LinkIcon, ChevronLeft, ChevronRight } from 'lucide-react'
import Breadcrumb from '@/components/ui/Breadcrumb'
import Newsletter from '@/components/sections/Newsletter'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import {
    usePublication,
    usePublications,
    useIncrementPublicationDownload,
    useIncrementPublicationView
} from '@/lib/api/hooks/publication'
import type { Publication } from '@/lib/api/types'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer, viewportReveal } from '@/lib/motion'
import { sanitizeHtml } from '@/utils/helpers/html'
import handleDownloadFromUrl from '@/utils/download'

// Default placeholder image for publications
const DEFAULT_PUBLICATION_IMAGE = 'https://res.cloudinary.com/dlwegkhme/image/upload/v1755762000/fb7ee40b478a3ecbe99ced8825e13470a61ce7d0_xze0jx.png'

// Helper function to format category from API to display format
const formatCategory = (category: string): string => {
    return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

// Helper function to format date
const formatDate = (dateString: string | null): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

export default function PublicationDetail() {
    const navigate = useNavigate()
    const { publicationId } = useParams<{ publicationId: string }>()

    const { data: publicationData, isLoading, error } = usePublication(publicationId)
    const { data: otherPublicationsData } = usePublications({ page: 1, size: 10 }, {})

    const incrementDownloadMutation = useIncrementPublicationDownload()
    const incrementViewMutation = useIncrementPublicationView()

    const apiPublication = publicationData?.data
    const allPublications = otherPublicationsData?.data?.data || []

    // Find current publication index for navigation
    const currentPublicationIndex = allPublications.findIndex((p: Publication) => p.publicationId === publicationId)
    const hasPrevious = currentPublicationIndex > 0
    const hasNext = currentPublicationIndex >= 0 && currentPublicationIndex < allPublications.length - 1
    const previousPublication = hasPrevious ? allPublications[currentPublicationIndex - 1] : null
    const nextPublication = hasNext ? allPublications[currentPublicationIndex + 1] : null

    // Get other publications for sidebar
    const otherPublications = allPublications
        .filter((p: Publication) => p.publicationId !== publicationId)
        .slice(0, 3)

    const [isDownloading, setIsDownloading] = useState(false)

    const handleDownload = async () => {
        if (!apiPublication?.publicationUrl) {
            alert('Publication document is not available for download.')
            return
        }

        setIsDownloading(true)
        try {
            // Increment download count
            await incrementDownloadMutation.mutateAsync(apiPublication.publicationId)

            // Import and use download utility
            // const { downloadPdf } = await import('../../utils/download')
            // await downloadPdf(apiPublication.publicationUrl, apiPublication.title)
            handleDownloadFromUrl({ url: apiPublication.publicationUrl, fileName: apiPublication.title })
        } catch (error) {
            console.error('Download error:', error)
            window.open(apiPublication.publicationUrl, '_blank', 'noopener,noreferrer')
        } finally {
            setIsDownloading(false)
        }
    }

    const handleView = async () => {
        if (!apiPublication?.publicationUrl) {
            alert('Publication document is not available.')
            return
        }

        try {
            // Increment view count
            await incrementViewMutation.mutateAsync(apiPublication.publicationId)
            // Open in new tab
            window.open(apiPublication.publicationUrl, '_blank')
        } catch (error) {
            console.error('Error viewing publication:', error)
            window.open(apiPublication.publicationUrl, '_blank')
        }
    }

    const handleShare = (platform: string) => {
        if (!apiPublication) return

        const url = encodeURIComponent(window.location.href)
        const title = encodeURIComponent(`${apiPublication.title} - African Space Agency`)

        switch (platform) {
            case 'facebook':
                window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank')
                break
            case 'twitter':
                window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`, '_blank')
                break
            case 'linkedin':
                window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank')
                break
            case 'copy':
                navigator.clipboard.writeText(window.location.href)
                alert('Link copied to clipboard!')
                break
            default:
                navigator.clipboard.writeText(window.location.href)
        }
    }

    if (isLoading) {
        return <LoadingSpinner fullScreen message="Loading publication..." />
    }

    if (error || !apiPublication) {
        return (
            <div className="bg-white min-h-screen">
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                    <div className="text-center py-12">
                        <p className="text-red-600 mb-4">Publication not found or error loading publication.</p>
                        <button
                            onClick={() => navigate('/publications')}
                            className="px-4 py-2 bg-wine-100 text-white rounded hover:bg-wine-200 transition-colors"
                        >
                            Back to Publications
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    const breadcrumbItems = [
        { label: 'Home', href: '/' },
        { label: 'Publications', href: '/publications' },
        { label: apiPublication.title, href: '#', active: true }
    ]

    return (
        <motion.div
            className="bg-white min-h-screen"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
        >
            <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-16" variants={fadeUp}>
                <div className="mb-5">
                    <Breadcrumb items={breadcrumbItems} />
                </div>

                <motion.div
                    className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-14"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={viewportReveal}
                >
                    {/* Main Content */}
                    <div className="lg:col-span-2 order-1">
                        <div className="space-y-12">
                            {/* Publication Header */}
                            <div className="space-y-6">
                                {/* Publication Image */}
                                <img
                                    src={apiPublication.coverImageUrl || DEFAULT_PUBLICATION_IMAGE}
                                    alt={apiPublication.title}
                                    className="w-full h-[300px] md:h-[400px] object-cover rounded-lg"
                                />

                                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-['PT_Serif'] text-gray-900 leading-tight">
                                    {apiPublication.title}
                                </h1>

                                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                                    <div className="flex items-center gap-1">
                                        <span className="text-base font-bold font-satoshi text-wine-100">Category</span>
                                        <span className="text-base font-normal font-satoshi text-gray-600"> - {formatCategory(apiPublication.publicationCategory)}</span>
                                    </div>
                                    {apiPublication.datePublished && (
                                        <div className="flex items-center gap-1">
                                            <span className="text-base font-bold font-satoshi text-wine-100">Published</span>
                                            <span className="text-base font-normal font-satoshi text-gray-600"> - {formatDate(apiPublication.datePublished)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Description */}
                            <div className="space-y-6">
                                {apiPublication.description && (
                                    <div
                                        className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed prose prose-lg max-w-none"
                                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(apiPublication.description) }}
                                    />
                                )}
                            </div>

                            {/* Actions */}
                            <div className="space-y-8">
                                <div className="flex flex-col sm:flex-row gap-4">
                                    {apiPublication.publicationUrl ? (
                                        <>
                                            <button
                                                onClick={handleDownload}
                                                disabled={isDownloading}
                                                className="px-6 py-2.5 bg-wine-100 rounded flex items-center justify-center gap-2 hover:bg-wine-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <Download className="w-4 h-4 text-white" />
                                                <span className="text-base font-bold font-satoshi text-white">
                                                    {isDownloading ? 'Downloading...' : 'Download'}
                                                </span>
                                            </button>
                                            <button
                                                onClick={handleView}
                                                disabled={incrementViewMutation.isPending}
                                                className="px-6 py-2.5 border border-gray-300 rounded flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <span className="text-base font-medium font-satoshi text-gray-900">
                                                    View Document
                                                </span>
                                            </button>
                                        </>
                                    ): null}

                                    {
                                        apiPublication.externalLink ? (
                                            <a
                                                href={apiPublication.externalLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-6 py-2.5 border border-gray-300 rounded bg-wine-100 text-white flex items-center justify-center gap-2 hover:bg-wine-200 transition-colors"
                                            >
                                               
                                                <LinkIcon className="w-4 h-4 " />
                                                    Open Link
                                              
                                              
                                              
                                            </a>
                                        ) : null
                                    }
                                </div>

                                {/* Social Share */}
                                <div className="space-y-4">
                                    <span className="text-lg font-normal font-['PT_Serif'] text-gray-600">
                                        SHARE
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleShare('facebook')}
                                            className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                                            aria-label="Share on Facebook"
                                        >
                                            <Facebook className="w-full h-full" />
                                        </button>
                                        <button
                                            onClick={() => handleShare('twitter')}
                                            className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                                            aria-label="Share on Twitter"
                                        >
                                            <Twitter className="w-full h-full" />
                                        </button>
                                        <button
                                            onClick={() => handleShare('linkedin')}
                                            className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                                            aria-label="Share on LinkedIn"
                                        >
                                            <Linkedin className="w-full h-full" />
                                        </button>
                                        <button
                                            onClick={() => handleShare('copy')}
                                            className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                                            aria-label="Copy link"
                                        >
                                            <LinkIcon className="w-full h-full" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Navigation */}
                            {!!(hasPrevious || hasNext) && (
                                <div className="flex items-center justify-between gap-4 pt-4 border-t border-gray-200">
                                    <button
                                        onClick={() => previousPublication && navigate(`/publications/${previousPublication.publicationId}`)}
                                        disabled={!hasPrevious}
                                        className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${hasPrevious
                                            ? 'text-gray-900 hover:bg-gray-200'
                                            : 'text-gray-400 cursor-not-allowed'
                                            }`}
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                        <span className="text-sm font-medium font-satoshi">Previous</span>
                                    </button>

                                    <button
                                        onClick={() => nextPublication && navigate(`/publications/${nextPublication.publicationId}`)}
                                        disabled={!hasNext}
                                        className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${hasNext
                                            ? 'text-gray-900 hover:bg-gray-200'
                                            : 'text-gray-400 cursor-not-allowed'
                                            }`}
                                    >
                                        <span className="text-sm font-medium font-satoshi">Next</span>
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1 order-2">
                        <div className="space-y-10">
                            {/* Other Publications */}
                            <div className="space-y-6">
                                <h3 className="text-xl sm:text-2xl font-normal font-['PT_Serif'] text-gray-900 leading-7">
                                    OTHER PUBLICATIONS
                                </h3>

                                <div className="space-y-4">
                                    {otherPublications.length > 0 ? (
                                        otherPublications.map((pub: Publication) => (
                                            <Link
                                                key={pub.publicationId}
                                                to={`/publications/${pub.publicationId}`}
                                                className="block bg-gray-50 hover:bg-wine-100 transition-colors overflow-hidden group"
                                            >
                                                <img
                                                    src={pub.coverImageUrl || DEFAULT_PUBLICATION_IMAGE}
                                                    alt={pub.title}
                                                    className="w-full h-32 object-cover"
                                                />
                                                <div className="p-4 space-y-2">
                                                    <p className="text-base font-medium font-satoshi text-gray-900 leading-normal line-clamp-2 group-hover:text-white transition-colors">
                                                        {pub.title}
                                                    </p>
                                                    <div className="flex items-center gap-2 text-sm text-gray-500 group-hover:text-white/80 transition-colors">
                                                        <span>{formatCategory(pub.publicationCategory)}</span>
                                                        {pub.datePublished && (
                                                            <>
                                                                <span>•</span>
                                                                <span>{formatDate(pub.datePublished)}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </Link>
                                        ))
                                    ) : (
                                        <p className="text-gray-600 text-sm">No other publications available.</p>
                                    )}
                                </div>
                            </div>

                            {/* Newsletter */}
                            <Newsletter />
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </motion.div>
    )
}
